import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile as firebaseUpdateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword as updatePasswordFn,
} from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { AuthUser } from '../types';

export interface AuthResponse {
  success: boolean;
  user?: AuthUser;
  error?: string;
}

export class AuthService {
  static async signIn(email: string, password: string): Promise<AuthResponse> {
    try {
      console.log('AuthService.signIn called with:', { email, password: '***' });
      
      // Validate inputs
      if (!email || !password) {
        console.error('Missing email or password');
        return {
          success: false,
          error: 'Email and password are required'
        };
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const user: AuthUser = {
          id: firebaseUser.uid,
          firebaseId: firebaseUser.uid,
          email: firebaseUser.email!,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          phoneNumber: userData.phoneNumber || '',
          role: userData.role || 'user',
          isActive: userData.isActive !== undefined ? userData.isActive : true,
          isEmailVerified: firebaseUser.emailVerified,
          isPhoneVerified: userData.isPhoneVerified !== undefined ? userData.isPhoneVerified : false,
          notificationPrefs: userData.notificationPrefs || {
            email: true,
            sms: false,
            push: true,
          },
          createdAt: userData.createdAt?.toDate() || new Date(),
          lastLoginAt: userData.lastLoginAt?.toDate(),
          profileImageUrl: userData.profileImageUrl,
        };
        
        // Update last login time
        await setDoc(doc(db, 'users', firebaseUser.uid), {
          lastLoginAt: Timestamp.now(),
        }, { merge: true });
        
        return {
          success: true,
          user,
        };
      } else {
        return {
          success: false,
          error: 'User profile not found',
        };
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code),
      };
    }
  }

  static async signOut(): Promise<AuthResponse> {
    try {
      await signOut(auth);
      return {
        success: true,
      };
    } catch (error: any) {
      console.error('Sign out error:', error);
      return {
        success: false,
        error: 'Failed to sign out',
      };
    }
  }

  static async createAdminUser(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    role: 'user' | 'admin' | 'super_admin' | 'client'
  ): Promise<AuthResponse> {
    try {
      console.log('Creating admin user:', { email, firstName, lastName, role });
      
      // Create user with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      console.log('Firebase user created:', firebaseUser.uid);
      
      // Update Firebase Auth profile
      await firebaseUpdateProfile(firebaseUser, {
        displayName: `${firstName} ${lastName}`,
      });
      
      console.log('User profile updated');
      
      // Create user document in Firestore
      const userData = {
        firebaseId: firebaseUser.uid,
        email: email,
        firstName: firstName,
        lastName: lastName,
        phoneNumber: phoneNumber,
        role: role,
        isActive: true,
        isEmailVerified: firebaseUser.emailVerified,
        isPhoneVerified: false,
        notificationPrefs: {
          email: true,
          sms: false,
          push: true,
        },
        createdAt: Timestamp.now(),
        lastLoginAt: null,
        profileImageUrl: null,
      };
      
      await setDoc(doc(db, 'users', firebaseUser.uid), userData);
      console.log('User document created in Firestore');
      
      const user: AuthUser = {
        id: firebaseUser.uid,
        firebaseId: firebaseUser.uid,
        email: email,
        firstName: firstName,
        lastName: lastName,
        phoneNumber: phoneNumber,
        role: role,
        isActive: true,
        isEmailVerified: firebaseUser.emailVerified,
        isPhoneVerified: false,
        notificationPrefs: {
          email: true,
          sms: false,
          push: true,
        },
        createdAt: new Date(),
        lastLoginAt: undefined,
        profileImageUrl: undefined,
      };
      
      console.log('Admin user created successfully');
      return {
        success: true,
        user,
      };
    } catch (error: any) {
      console.error('Create user error:', error);
      
      // If user already exists, try to sign in instead
      if (error.code === 'auth/email-already-in-use') {
        console.log('Admin user already exists, attempting to sign in...');
        return await this.signIn(email, password);
      }
      
      return {
        success: false,
        error: this.getErrorMessage(error.code),
      };
    }
  }

  static async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        return null;
      }
      
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          id: firebaseUser.uid,
          firebaseId: firebaseUser.uid,
          email: firebaseUser.email!,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          phoneNumber: userData.phoneNumber || '',
          role: userData.role || 'user',
          isActive: userData.isActive !== undefined ? userData.isActive : true,
          isEmailVerified: firebaseUser.emailVerified,
          isPhoneVerified: userData.isPhoneVerified !== undefined ? userData.isPhoneVerified : false,
          notificationPrefs: userData.notificationPrefs || {
            email: true,
            sms: false,
            push: true,
          },
          createdAt: userData.createdAt?.toDate() || new Date(),
          lastLoginAt: userData.lastLoginAt?.toDate(),
          profileImageUrl: userData.profileImageUrl,
        };
      }
      
      return null;
    } catch (error: any) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  static async updateProfile(profile: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  }): Promise<AuthResponse> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        return { success: false, error: 'No authenticated user' };
      }

      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber,
        updatedAt: Timestamp.now(),
      });

      await firebaseUpdateProfile(firebaseUser, {
        displayName: `${profile.firstName} ${profile.lastName}`.trim(),
      });

      const refreshed = await this.getCurrentUser();
      return { success: true, user: refreshed || undefined };
    } catch (error: any) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message || 'Failed to update profile' };
    }
  }

  static async updateNotificationPrefs(prefs: {
    email: boolean;
    sms: boolean;
    push: boolean;
  }): Promise<AuthResponse> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        return { success: false, error: 'No authenticated user' };
      }

      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        notificationPrefs: prefs,
        updatedAt: Timestamp.now(),
      });

      const refreshed = await this.getCurrentUser();
      return { success: true, user: refreshed || undefined };
    } catch (error: any) {
      console.error('Update notification prefs error:', error);
      return { success: false, error: error.message || 'Failed to update notifications' };
    }
  }

  static async updatePassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || !firebaseUser.email) {
        return { success: false, error: 'No authenticated user' };
      }

      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      await updatePasswordFn(firebaseUser, newPassword);

      return { success: true };
    } catch (error: any) {
      console.error('Update password error:', error);
      return { success: false, error: this.getErrorMessage(error.code) || 'Failed to update password' };
    }
  }

  private static getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No user found with this email address';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/email-already-in-use':
        return 'Email address is already in use';
      case 'auth/weak-password':
        return 'Password is too weak';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      default:
        return 'An error occurred. Please try again';
    }
  }
}
